import { useEffect, useRef, useState, useCallback } from "react";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { OrgSessionEvents } from "@platform/constants/Events";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { eventBus } from "@platform/events/EventBus";

import { normalizeEvent } from "./normalizeEvent";
import type { DeploymentEvent } from "./useDeploymentEvents";
import { useStackRealtime } from "./useStackRealtime";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineStep {
  id: number;
  systemId: number;
  systemAlias: string;
  step: string;
  serviceName: string;
  status: string;
  severity: string;
  message: string;
  eventType: string;
  sagaId: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
  executions?: Array<{
    id: number;
    name: string;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
    logRef: string | null;
  }>;
}

export interface WsDeploymentEvent {
  jobId: string;
  systemId: number;
  systemAlias: string;
  organizationId: number;
  timestamp: string;
  severity: "info" | "warning" | "error";
  message: string;
  eventType: string;
  step: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface WsLogBatchEvent {
  sagaId: string;
  service: string;
  phase: string;
  offset: number;
  lines: string[];
  systemId?: number;
  systemAlias?: string;
  organizationId?: number;
}

export type UseDeploymentStreamResult = {
  events: DeploymentEvent[];
  pipelineState: PipelineStep[];
  /** Live log batches keyed by sagaId → service → phase → sorted lines */
  logBatches: Map<string, Map<string, Map<string, string[]>>>;
  loading: boolean;
  error: string | null;
  /** True when the error is a 403 org-mismatch — the user needs to switch orgs */
  orgMismatch: boolean;
  connected: boolean;
  refetch: () => void;
  /** Increments each time a pipeline_run WS event arrives (new run created or status updated).
   *  Consumers can use this as a re-fetch trigger for their run lists. */
  pipelineRunRefreshSignal: number;
};

/**
 * Real-time deployment pipeline event stream.
 *
 * 1. On mount: fetches historical events via REST
 * 2. Opens Socket.io connection to /deployment-events namespace
 * 3. Subscribes to systemAlias — backend streams events as they occur
 * 4. On unmount: unsubscribes and disconnects
 */
export function useDeploymentStream(
  alias: string | undefined,
): UseDeploymentStreamResult {
  // ── Context consumption ──────────────────────────────────────────────────
  // If inside a StackRealtimeProvider, consume shared data instead of
  // creating a separate WS connection. This reduces 5 connections → 2.
  const ctx = useStackRealtime();

  // ── Fallback: always declare hooks to satisfy rules-of-hooks ──────────
  const [events, setEvents] = useState<DeploymentEvent[]>([]);
  const [pipelineState, setPipelineState] = useState<PipelineStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgMismatch, setOrgMismatch] = useState(false);
  const [connected, setConnected] = useState(false);
  /** Live log batches: Map<sagaId, Map<service, Map<phase, sorted lines>>> */
  const [logBatches, setLogBatches] = useState<
    Map<string, Map<string, Map<string, string[]>>>
  >(new Map());
  const [pipelineRunRefreshSignal, setPipelineRunRefreshSignal] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  // Debounce timer for batching rapid WS-driven pipelineState re-fetches
  const pipelineStateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const fetchPipelineState = useCallback(async () => {
    if (!alias) return [];
    try {
      const res = await axios.get<PipelineStep[]>(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/pipeline-state`,
        { withCredentials: true },
      );
      return res.data;
    } catch {
      return [];
    }
  }, [alias]);

  const fetchEvents = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    setOrgMismatch(false);
    try {
      const url = `${apiBaseUrls.BACKEND}/api/v1/deployment-events/system/${alias}`;
      const res = await axios.get<{ events: DeploymentEvent[]; total: number }>(
        url,
        { withCredentials: true },
      );
      setEvents(res.data.events.map(normalizeEvent));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setOrgMismatch(true);
        setError(
          "This system belongs to a different organization. Switch organizations to view pipeline events.",
        );
      } else {
        setError("Deployment events not available");
      }
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    if (!alias) return;

    // Fetch pipeline state (materialized view) and events in parallel
    void Promise.all([fetchEvents(), fetchPipelineState()]).then(
      ([_eventsRes, pipelineRes]) => {
        setPipelineState(pipelineRes);
      },
    );

    // If inside a StackRealtimeProvider, don't open a WebSocket —
    // the SSE context provides all real-time data.
    if (ctx) return;

    // Open WebSocket — connect to the /deployment-pipeline-events namespace
    const socket = io(`${apiBaseUrls.BACKEND}/deployment-pipeline-events`, {
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 20,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("subscribe", { systemAlias: alias });
    });

    socket.on("deployment", (data: WsDeploymentEvent) => {
      // Convert WS event to DeploymentEvent shape and prepend
      const raw: DeploymentEvent = {
        id: `${data.jobId}-${data.eventType}-${data.timestamp}`,
        sagaId: data.jobId,
        eventType: data.eventType,
        systemId: data.systemId,
        systemAlias: data.systemAlias,
        organizationId: data.organizationId,
        step: data.step,
        status:
          data.status ?? (data.severity === "error" ? "failed" : "in_progress"),
        severity: data.severity,
        message: data.message,
        metadata: data.metadata,
        retryCount: 0,
        createdAt: data.timestamp,
        updatedAt: data.timestamp,
      };
      const event = normalizeEvent(raw);
      setEvents((prev) => [event, ...prev]);
      setError(null);

      // Debounce pipelineState re-fetches: batch rapid WS events within a
      // 300ms window to reduce re-render flicker and avoid redundant API calls.
      if (pipelineStateTimerRef.current) {
        clearTimeout(pipelineStateTimerRef.current);
      }
      pipelineStateTimerRef.current = setTimeout(() => {
        pipelineStateTimerRef.current = null;
        void fetchPipelineState().then((pipelineRes) => {
          setPipelineState(pipelineRes);
        });
      }, 100);
    });

    // Handle live log batch events from build/deploy services
    // Scoped by sagaId so different runs don't mix logs
    socket.on("deployment.log", (data: WsLogBatchEvent) => {
      setLogBatches((prev) => {
        const next = new Map(prev);
        const sagaMap = new Map(
          next.get(data.sagaId) ?? new Map<string, Map<string, string[]>>(),
        );
        const serviceMap = new Map(
          sagaMap.get(data.service) ?? new Map<string, string[]>(),
        );
        const existingLines = serviceMap.get(data.phase) ?? [];
        // Append new lines starting from the offset
        const merged =
          data.offset <= existingLines.length
            ? [...existingLines.slice(0, data.offset), ...data.lines]
            : [...existingLines, ...data.lines];
        serviceMap.set(data.phase, merged);
        sagaMap.set(data.service, serviceMap);
        next.set(data.sagaId, sagaMap);
        return next;
      });
    });

    socket.on("error", (data: { message: string }) => {
      setError(data.message);
    });

    // Pipeline run created/updated — triggers run list refetch in consumers
    socket.on("pipeline_run", () => {
      setPipelineRunRefreshSignal((n) => n + 1);
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    return () => {
      if (pipelineStateTimerRef.current) {
        clearTimeout(pipelineStateTimerRef.current);
        pipelineStateTimerRef.current = null;
      }
      socket.emit("unsubscribe", { systemAlias: alias });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [alias, fetchEvents, ctx]);

  // Re-fetch on mid-session org switch
  useEffect(() => {
    const handler = () => {
      void Promise.all([fetchEvents(), fetchPipelineState()]).then(
        ([_eventsRes, pipelineRes]) => {
          setPipelineState(pipelineRes);
        },
      );
    };
    eventBus.subscribeGlobal(OrgSessionEvents.ORG_SESSION_CHANGED, handler);
  }, [fetchEvents, fetchPipelineState]);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      events: ctx.events,
      pipelineState: ctx.pipelineState,
      logBatches: ctx.logBatches,
      loading: ctx.deploymentLoading,
      error: ctx.deploymentError,
      orgMismatch: ctx.orgMismatch,
      connected: ctx.deploymentConnected,
      refetch: ctx.refetchEvents,
      pipelineRunRefreshSignal: ctx.pipelineRunRefreshSignal,
    };
  }

  return {
    events,
    pipelineState,
    logBatches,
    loading,
    error,
    orgMismatch,
    connected,
    refetch: fetchEvents,
    pipelineRunRefreshSignal,
  };
}
